/**
 * The run of other tokens along the top of a token page.
 *
 * Both terminals we are measured against put one directly under the nav, and
 * the reason is not decoration: a token page is the one screen in the product
 * where the whole rest of the market is invisible, and a page about a live
 * market should never look like the only thing moving is the thing you are
 * looking at.
 *
 * The rows come from the trending read, which returns whole pool rows. Only
 * four fields of each survive into a chip, so the mapping is here rather than
 * in the component, and it is a plain function so the awkward parts — a row
 * with no symbol, a cap that arrived as a string, the token already on screen —
 * can be said in a test rather than guessed at.
 */

/** The little a chip needs, named so the reading does not depend on the DTO. */
export type TickerRow = {
  poolAddress: string
  quote: { symbol?: string | null; logo?: string | null }
  marketCapUsd?: string | number | null
  periodStats?: { '1D'?: { priceChangePercents?: string | number | null } }
}

export type TickerItem = {
  key: string
  ticker: string
  /** Empty when the token has no picture; the mark falls back to a letter. */
  logo: string
  capUsd: number
  changePercent: number
}

/** A figure that arrived as a string, or did not arrive at all. */
const numberOf = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) {
    return Number.NaN
  }
  const read = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(read) ? read : Number.NaN
}

const DEFAULT_LIMIT = 12

export const tickerItems = (
  rows: ReadonlyArray<TickerRow>,
  { exclude, limit = DEFAULT_LIMIT }: { exclude?: string; limit?: number } = {},
): ReadonlyArray<TickerItem> => {
  const seen = new Set<string>()

  return rows
    .filter((row) => {
      /* A pool with no symbol would render as a blank chip, and the token
         whose page this is does not belong in a list of other tokens. */
      const ticker = row.quote.symbol?.trim()
      if (!ticker || row.poolAddress === exclude) {
        return false
      }
      if (seen.has(row.poolAddress)) {
        return false
      }
      seen.add(row.poolAddress)
      return true
    })
    .slice(0, limit)
    .map((row) => ({
      key: row.poolAddress,
      ticker: (row.quote.symbol ?? '').trim(),
      logo: row.quote.logo ?? '',
      capUsd: numberOf(row.marketCapUsd),
      changePercent: numberOf(row.periodStats?.['1D']?.priceChangePercents),
    }))
}
